<template>
  <div class="scratch-card">
    <div
      class="fill-height fill-width prize-content"
      :class="{ revealed: isRevealed }"
    >
      <v-img :src="require('@/assets/home/sunburst.png')" height="100%">
        <div class="d-flex flex-column fill-height fill-width justify-center">
          <template v-if="scratch.result?.matrix?.length">
            <div
              v-for="(row, i) in scratch?.result?.matrix"
              class="d-flex justify-space-around fill-height"
              :key="i"
            >
              <div
                v-for="(item, n) in row"
                :key="n"
                class="d-flex align-center justify-center fill-height"
              >
                <div
                  class="prize-wrapper"
                  :style="{ 'border-color': item.color }"
                >
                  <div class="cyan darken-3 rounded-lg">
                    <v-avatar
                      color=""
                      :class="
                        fetchPrized(item)
                          ? 'cyan accent-4 pulse-prize'
                          : 'cyan darken-2 transparent-prize'
                      "
                      :style="{ '--pulse-color': item.color }"
                      size="4rem"
                      rounded
                    >
                      <v-img
                        :src="
                          item.image ||
                          (item.type === 'event'
                            ? require(`@/assets/prizes/${item.event}.png`)
                            : require(`@/assets/prizes/${item.type}.png`))
                        "
                        contain
                      >
                        <div
                          class="d-flex flex-column fill-height fill-width full-width"
                        >
                          <v-spacer />

                          <div
                            v-if="item.label?.text"
                            class="text-center img-content"
                          >
                            <div
                              class="value-text text--lighten-2 font-italic russo-one"
                              :style="`color: ${item.label.fontColor} !important; text-shadow: -0.0625rem -0.0625rem 0 ${item.label.borderColor}, 0.0625rem -0.0625rem 0 ${item.label.borderColor}, -0.0625rem 0.0625rem 0 ${item.label.borderColor}, 0.0625rem 0.0625rem 0 ${item.label.borderColor} !important;`"
                            >
                              {{ item.label.text }}
                            </div>
                          </div>
                        </div>
                      </v-img>
                    </v-avatar>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <v-icon color="cyan darken-1" size="10rem">
              fas fa-question
            </v-icon>
          </template>
        </div>

        <v-overlay absolute :value="loading"> </v-overlay>
      </v-img>
    </div>

    <canvas
      v-if="!isRevealed"
      ref="scratchCanvas"
      class="scratch-layer"
      :class="{ hidden: isRevealed }"
      @mousedown="startScratching"
      @mousemove="onScratch"
      @mouseup="stopScratching"
      @touchstart="startScratching"
      @touchmove="onScratch"
      @touchend="stopScratching"
    ></canvas>
  </div>
</template>

<script>
export default {
  name: "ScratchCard",

  props: {
    scratch: {
      type: Object,
      default: () => {},
    },

    revealThreshold: {
      type: Number,
      default: 60,
    },

    brushSize: {
      type: Number,
      default: 60,
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      canvas: null,
      ctx: null,
      started: false,
      isScratching: false,
      lastX: null,
      lastY: null,
      canvasRect: null,
      scratchPercentage: 0,
      isRevealed: false,
    };
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("mousemove", this.handleGlobalMouseMove);
    window.addEventListener("mouseup", this.handleGlobalMouseUp);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("mousemove", this.handleGlobalMouseMove);
    window.removeEventListener("mouseup", this.handleGlobalMouseUp);
  },

  methods: {
    initCanvas() {
      if (!this.$refs.scratchCanvas) return;

      this.canvas = this.$refs.scratchCanvas;
      this.ctx = this.canvas.getContext("2d");

      const container = this.canvas.parentElement;
      this.canvas.width = container.clientWidth;
      this.canvas.height = container.clientHeight;

      this.drawScratchLayer();
    },

    handleResize() {
      if (!this.canvas) return;

      setTimeout(() => {
        this.initCanvas();
      }, 100);
    },

    drawScratchLayer() {
      if (!this.ctx) return;

      this.ctx.globalCompositeOperation = "source-over";
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      const image = new Image();
      image.src = require("@/assets/scratch/scratch-cover.png");

      image.onload = () => {
        this.ctx.drawImage(image, 0, 0, this.canvas.width, this.canvas.height);
        this.ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
        this.ctx.font = "900 28px Arial Black, Helvetica, sans-serif";
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.fillText(
          "RASPE AQUI",
          this.canvas.width / 2,
          this.canvas.height / 2
        );
      };
    },

    startScratching(e) {
      this.handleStarted();
      this.isScratching = true;
      this.canvasRect = this.canvas.getBoundingClientRect();
      const { x, y } = this.getCoordinates(e);
      this.lastX = x;
      this.lastY = y;
      this.onScratch(e);
    },

    handleStarted() {
      if (!this.started) {
        this.$emit("started");
        this.started = true;
      }
    },

    stopScratching() {
      this.isScratching = false;
      this.lastX = null;
      this.lastY = null;
      this.checkReveal();
    },

    handleGlobalMouseMove(e) {
      if (this.isScratching) this.onScratch(e);
    },

    handleGlobalMouseUp() {
      if (this.isScratching) this.stopScratching();
    },

    onScratch(e) {
      if (!this.isScratching || !this.ctx) return;
      this.canvasRect = this.canvas.getBoundingClientRect();
      let clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
      let clientY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;

      if (
        clientX < this.canvasRect.left ||
        clientX > this.canvasRect.right ||
        clientY < this.canvasRect.top ||
        clientY > this.canvasRect.bottom
      )
        return;

      const { x, y } = this.getCoordinates(e);
      this.ctx.globalCompositeOperation = "destination-out";

      if (this.lastX !== null && this.lastY !== null) {
        this.ctx.beginPath();
        this.ctx.lineWidth = this.brushSize;
        this.ctx.lineCap = "round";
        this.ctx.moveTo(this.lastX, this.lastY);
        this.ctx.lineTo(x, y);
        this.ctx.stroke();
      }

      this.ctx.beginPath();
      this.ctx.arc(x, y, 15, 0, Math.PI * 2);
      this.ctx.fill();

      this.lastX = x;
      this.lastY = y;
      this.calculateScratchPercentage();
      this.$emit("scratching", this.scratchPercentage);
    },

    getCoordinates(e) {
      if (!this.canvasRect)
        this.canvasRect = this.canvas.getBoundingClientRect();
      const clientX = e.type.includes("touch")
        ? e.touches[0].clientX
        : e.clientX;
      const clientY = e.type.includes("touch")
        ? e.touches[0].clientY
        : e.clientY;
      const scaleX = this.canvas.width / this.canvasRect.width;
      const scaleY = this.canvas.height / this.canvasRect.height;
      return {
        x: (clientX - this.canvasRect.left) * scaleX,
        y: (clientY - this.canvasRect.top) * scaleY,
      };
    },

    calculateScratchPercentage() {
      if (!this.ctx) return;
      const imageData = this.ctx.getImageData(
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      const pixels = imageData.data;
      let transparentPixels = 0,
        totalPixels = 0;
      for (let y = 0; y < this.canvas.height; y += 2) {
        for (let x = 0; x < this.canvas.width; x += 2) {
          const index = (y * this.canvas.width + x) * 4;
          if (pixels[index + 3] === 0) transparentPixels++;
          totalPixels++;
        }
      }
      this.scratchPercentage = Math.round(
        (transparentPixels / totalPixels) * 100
      );
      this.checkReveal();
    },

    async checkReveal() {
      if (this.loading) {
        await new Promise((resolve) => {
          const unwatch = this.$watch("loading", (newVal) => {
            if (!newVal) {
              unwatch();
              resolve();
            }
          });
        });
      }

      if (this.scratchPercentage >= this.revealThreshold && !this.isRevealed) {
        this.isRevealed = true;
        this.$emit("revealed");

        if (this.canvas) {
          this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
      }
    },

    resetScratch() {
      this.started = false;
      this.isRevealed = false;
      this.scratchPercentage = 0;
      this.lastX = null;
      this.lastY = null;
      this.canvasRect = null;
      this.$nextTick(() => this.initCanvas());
    },

    fetchPrized(item) {
      if (!this.isRevealed) return false;

      if (this.scratch.result?.event === "nothing") {
        return this.scratch.result?.event === item.event;
      }

      if (
        this.scratch.result?.type === "money" ||
        this.scratch.result?.type === "points"
      ) {
        return (
          this.scratch.result?._id === item._id &&
          this.scratch.result?.value === item.value
        );
      }

      if (this.scratch.result?.type === "item") {
        return (
          this.scratch.result?._id === item._id &&
          this.scratch.result?.qty === item.qty
        );
      }

      return this.scratch.result?.item?._id === item.item?._id;
    },
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
